/*
 * File: src/routes/register/EnterCard.tsx
 * Notes:
 *   > ...
 */

import React, { VoidFunctionComponent } from "react";
import Typography from "@material-ui/core/Typography";
import { gql, useApolloClient } from "@apollo/client";
import makeStyles from "@material-ui/core/styles/makeStyles";
import CardMaskedInput from "../../../components/inputs/masks/CardMaskedInput";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CardSchema, Require } from "../../../validation/schema";
import { useSnackbar } from "notistack";
import MuiLink from "@material-ui/core/Link";
import { Link } from "react-router-dom";
import ProgressButton from "../../../components/button/ProgressButton";
import FormikTextField from "../../../components/inputs/FormikTextField";
import { GENERIC_ERROR } from "../../../constants/strings";
import getApolloErrorMessage from "../../../gql/getApolloErrorMessage";

const useStyles = makeStyles((theme) => ({
  green: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  blue: {
    fontWeight: "bold",
    color: theme.palette.secondary.main,
  },
  container: {
    "& > *:not(:last-child)": {
      marginBottom: theme.spacing(2),
    },
  },
}));

const Query = gql`
  query ($card: String!) {
    cardRegistrationStatus(card: $card)
  }
`;

const schema = Yup.object({
  card: Require(CardSchema.default("")),
});

const EnterCard: VoidFunctionComponent = () => {
  const classes = useStyles();

  const history = useHistory();

  const client = useApolloClient();

  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: { card: "", hasCard: "yes" },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        const { data } = await client.query({
          query: Query,
          variables: {
            card: values.card,
          },
          fetchPolicy: "no-cache",
        });

        if (data) {
          const { cardRegistrationStatus: status } = data;

          if (status === "EXISTING") {
            history.push({ pathname: "/register" }, { card: values.card });
          } else if (status === "INVALID") {
            formik.setFieldError("card", "Card not recognized.");
          } else if (status === "NEW") {
            history.push(
              { pathname: "/create-account" },
              { card: values.card }
            );
          } else {
            formik.setFieldError(
              "card",
              "This card has already been registered for online access."
            );
          }
        } else {
          enqueueSnackbar(GENERIC_ERROR, {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar(getApolloErrorMessage(error), {
          variant: "error",
        });
      }
    },
  });

  return (
    <form
      className={classes.container}
      onSubmit={(event) => {
        event.preventDefault();
        formik.submitForm();
      }}
    >
      <Typography>
        The signup form below allows existing CLYNK account holders who signed
        up at a kiosk to set up login credential for online access to their
        CLYNK account. CLYNK Online Access allows you to check your balance,
        change your PIN and Password, update your profile, understand the impact
        your recycling efforts as well as donate to local charities--- on the go
        on your smart phone or at a computer!
      </Typography>

      <FormikTextField
        form={formik}
        label="Card Number"
        field={formik.getFieldProps("card")}
        fullWidth={false}
        InputProps={{
          inputComponent: CardMaskedInput,
          inputProps: { inputMode: "numeric" },
        }}
        style={{ maxWidth: "100%", width: "200px", display: "block" }}
      />

      <ProgressButton
        type="submit"
        variant="contained"
        color="primary"
        LinearProgressProps={{ color: "secondary" }}
        loading={formik.isSubmitting}
        disabled={formik.isSubmitting}
      >
        Continue
      </ProgressButton>

      <Typography>
        If you do not already have a CLYNK account, click{" "}
        {/* Part of CLNK-1854 (JIRA ticket): Originally linked to /create-card/enter-card */}
        <MuiLink component={Link} to="/create-account">
          here
        </MuiLink>
        .
      </Typography>

      <input type="submit" style={{ display: "none" }} />
    </form>
  );
};

export default EnterCard;
